import React, { useContext, useRef, useState } from 'react'
import { Button, ListMenu, ListMenuButton,
   NotificationManagerContext, Modal, Text, View } from 'oio-react'
import PropTypes from 'prop-types'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { MoreIcon } from 'assets/icons'
import uiConstants from 'config/constants/ui'
import ObjectDeleteConfirm from 'src/sites/kits/Object/components/DeleteConfirm'
import Popover from 'src/sites/kits/Utils/Popover'
import { useMe, useOrganization, useUpdateInitiative,
   useUpdateRelationship } from 'src/core/graphql/hooks'
import { InitiativeHierarchyContext } from 'src/sites/kits/Utils/InitiativeHierarchy'

const unpublishedPrivacyValueMap = {
   discussion: 'draft',
   page: 'admin',
   post: 'admin'
}

const InitiativeMoreButton = ({
   buttonRounded,
   buttonSize,
   buttonWidth,
   color,
   editUrl,
   humanizedTypeNameSingular: customHumanizedTypeNameSingular,
   iconColor,
   iconSize,
   popoverAnchorOriginVertical,
   showInitiativeName
}) => {
   const history = useHistory()
   const match = useRouteMatch()
   const popoverButton = useRef()
   const [modalIsOpen, setModalIsOpen] = useState(false)
   const { showNotification } = useContext(NotificationManagerContext)

   const { me, isLoggedIn } = useMe()
   const { organization } = useOrganization()
   const { initiative, breadcrumbUrls } = useContext(InitiativeHierarchyContext)
   const { updateInitiative, mutating } = useUpdateInitiative()
   const { updateRelationship } = useUpdateRelationship()

   const parentInitiative = initiative.ancestors[0]
   const humanizedTypeNameSingular = customHumanizedTypeNameSingular || initiative.type.nameSingular
   const isBleedingEdge = organization.gatherAppVersion === 6
   const isActive = ['inherit', 'private'].includes(initiative.privacy)
   const isFavorited = initiative.currentUserRelationship?.isFavorited
   const isDynamicPage = initiative.pageFormat === 'dynamic'
   const userCanEditInitiative = initiative.currentUserCanEdit
   const userCanRemoveInitiative = initiative.currentUserCanRemove
   const userCannotEditOrRemove = !userCanEditInitiative && !userCanRemoveInitiative
   const userIsOrganizationAdmin = organization.currentUserIsAdmin
   const isPinnable = isActive &&
      userIsOrganizationAdmin &&
      ['discussion', 'group', 'post'].includes(initiative.class)

   // For initiatives that appear to have more of a binary published/unpublished state
   let isPublishable = Object.keys(unpublishedPrivacyValueMap).includes(initiative.class)
   if (parentInitiative) {
      isPublishable = isPublishable &&
         parentInitiative.class !== 'session' &&
         ['inherit', 'private'].includes(parentInitiative.privacy)
   }

   // TODO: this will change - we will add initiative options for all logged in users
   // Hide button if user cannot remove or edit initiative
   // PR #768
   if (!isLoggedIn || userCannotEditOrRemove) {
      return null
   }

   const handleUpdateInitiative = async (value) => {
      try {
         await updateInitiative({ id: initiative.id }, value)

         showNotification({
            message: `${humanizedTypeNameSingular} updated successfully`,
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   const handleToggleBookmarked = async () => {
      try {
         await updateRelationship({
            entityType: 'initiative',
            entityId: initiative.id,
            userId: me.id,
            elements: [{
               operation: 'favorite',
               favorite: !isFavorited
            }]
         })

         showNotification({
            message: isFavorited
               ? 'Removed from Bookmarks'
               : 'Added to Bookmarks',
            title: 'Success!',
            type: 'success'
         })
      } catch (err) {
         showNotification({
            message: err.message,
            title: 'Error!',
            type: 'error'
         })
      }
   }

   const handleOnDelete = () => {
      const parentInitiativeUrl = breadcrumbUrls[breadcrumbUrls.length - 2]

      if (parentInitiativeUrl) {
         return history.push(parentInitiativeUrl)
      }

      // TODO: Still need to figure out a better way to handle deleting root initiatives
      // PR #844
      return history.push('/-/profile')
   }

   return (
      <View marginLeft="3px">
         <Popover.Provider>
            <Popover.Anchor>
               <Button
                  ref={popoverButton}
                  color={mutating ? '#666' : color}
                  mode={mutating ? 'loading' : 'normal'}
                  rounded={buttonRounded}
                  size={buttonSize}
                  width={buttonWidth}>
                  <MoreIcon width={iconSize} height={iconSize} color={iconColor} />
               </Button>
            </Popover.Anchor>
            <Popover.View
               anchorElement={popoverButton.current}
               anchorOriginHorizontal="right"
               anchorOriginVertical={popoverAnchorOriginVertical}
               borderRadius="6px"
               width="220px">
               <View
                  float="left"
                  width="100%"
                  borderRadius="6px"
                  margin="12px 0px"
                  backgroundColor="#fff"
                  border="1px solid #eee"
                  boxShadow="6px 6px 30px rgba(0,0,0,0.1)">
                  {showInitiativeName && (
                     <View
                        float="left"
                        width="100%"
                        padding="10px 15px"
                        borderBottom="1px solid #eee">
                        <Text size="1.5" weight="medium">
                           {initiative.name}
                        </Text>
                     </View>
                  )}
                  <View float="left" width="100%">
                     <ListMenu buttonSize="sm">
                        {isBleedingEdge && (
                           <ListMenuButton
                              onClick={handleToggleBookmarked}
                              name={isFavorited
                                 ? 'Remove from Bookmarks'
                                 : 'Add to Bookmarks'
                              }
                           />
                        )}
                        {userCanEditInitiative && (
                           <ListMenuButton
                              linkTo={editUrl || `${match.url}/-/settings`}
                              name={`Edit this ${humanizedTypeNameSingular}`}
                           />
                        )}
                        {userCanEditInitiative && isDynamicPage && (
                           <ListMenuButton
                              linkTo={`${match.url}/-/manage-content`}
                              name="Manage Content"
                           />
                        )}
                        {isPublishable && (
                           <ListMenuButton
                              onClick={() => {
                                 handleUpdateInitiative({
                                    privacy: ['inherit', 'private'].includes(initiative.privacy)
                                       ? unpublishedPrivacyValueMap[initiative.class]
                                       : 'inherit'
                                 })
                              }}
                              name={['inherit', 'private'].includes(initiative.privacy)
                                 ? `Unpublish this ${humanizedTypeNameSingular}`
                                 : `Publish ${humanizedTypeNameSingular}`
                              }
                           />
                        )}
                        {isPinnable && (
                           <ListMenuButton
                              onClick={() => {
                                 handleUpdateInitiative({
                                    pinned: !initiative.pinned
                                 })
                              }}
                              name={initiative.pinned
                                 ? `Un-pin ${humanizedTypeNameSingular}`
                                 : `Pin this ${humanizedTypeNameSingular}`
                              }
                           />
                        )}
                        {userCanEditInitiative && initiative.class === 'event' && isActive && (
                           <ListMenuButton
                              linkTo={`${match.url}/-/checkin`}
                              name="Check-in Desk"
                           />
                        )}
                        {userCanEditInitiative && initiative.class === 'discussion' && isActive && (
                           <ListMenuButton
                              onClick={() => {
                                 handleUpdateInitiative({
                                    discussionStatus: {
                                       closed: !initiative.discussionStatus.closed
                                    }
                                 })
                              }}
                              name={initiative.discussionStatus.closed
                                 ? `Re-open ${humanizedTypeNameSingular}`
                                 : `Close this ${humanizedTypeNameSingular}`
                              }
                           />
                        )}
                        {userCanRemoveInitiative && (
                           <ListMenuButton
                              onClick={() => setModalIsOpen(true)}
                              name={`Delete this ${humanizedTypeNameSingular}`}
                           />
                        )}
                     </ListMenu>
                  </View>
               </View>
            </Popover.View>
         </Popover.Provider>
         <Modal
            borderRadius="6px"
            width="90%[a-b] 300px[c-f]"
            onCloseTrigger={() => setModalIsOpen(false)}
            open={modalIsOpen}
            zIndex={uiConstants.zIndexes.deleteConfirmModal}>
            <ObjectDeleteConfirm
               activeSubInitiativeTypeNames={initiative.activeSubInitiativeTypeNames}
               initiativeId={initiative.id}
               initiativeName={initiative.name}
               onCancelButtonClick={() => setModalIsOpen(false)}
               onDelete={handleOnDelete}
            />
         </Modal>
      </View>
   )
}

InitiativeMoreButton.propTypes = {
   buttonRounded: PropTypes.bool,
   buttonSize: PropTypes.string,
   buttonWidth: PropTypes.string,
   color: PropTypes.string,
   editUrl: PropTypes.string,
   humanizedTypeNameSingular: PropTypes.string,
   iconColor: PropTypes.string,
   iconSize: PropTypes.string,
   popoverAnchorOriginVertical: PropTypes.string,
   showInitiativeName: PropTypes.bool
}

InitiativeMoreButton.defaultProps = {
   buttonRounded: false,
   buttonSize: 'md',
   buttonWidth: '36px',
   color: '#eee',
   editUrl: undefined,
   humanizedTypeNameSingular: undefined,
   iconColor: '#444',
   iconSize: '18px',
   popoverAnchorOriginVertical: 'bottom',
   showInitiativeName: false
}

export default InitiativeMoreButton
